// ---------------------------------------------------------------------------
// Block-level Markdown detection for tool-call compaction.
//
// Assistant updates that contain a block construct are treated as a visible
// response boundary. Plain prose and inline Markdown stay with the compacted
// tool run, where they read as iterative narration rather than a response.
// ---------------------------------------------------------------------------

const ATX_HEADING_RE = /^[ \t]{0,3}#{1,6}(?:[ \t]+|$)/;
const FENCED_CODE_BLOCK_RE = /^[ \t]{0,3}(?:`{3,}|~{3,})/;
const BLOCKQUOTE_RE = /^[ \t]{0,3}>/;
const SETEXT_UNDERLINE_RE = /^[ \t]{0,3}[=-]+[ \t]*$/;
const HORIZONTAL_RULE_RE =
  /^[ \t]{0,3}(?:(?:\*[ \t]*){3,}|(?:-[ \t]*){3,}|(?:_[ \t]*){3,})$/;

// These are HTML elements that Markdown parsers render as blocks when they
// begin a line. Keep this deliberately to block elements; inline HTML such as
// <span> should not turn internal narration into a visible response boundary.
const HTML_BLOCK_TAGS = [
  "address",
  "article",
  "aside",
  "blockquote",
  "details",
  "dialog",
  "div",
  "dl",
  "fieldset",
  "figcaption",
  "figure",
  "footer",
  "form",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "header",
  "hr",
  "main",
  "menu",
  "nav",
  "ol",
  "p",
  "pre",
  "section",
  "table",
  "tbody",
  "td",
  "tfoot",
  "th",
  "thead",
  "tr",
  "ul",
].join("|");

const HTML_BLOCK_RE = new RegExp(
  `^[ \\t]{0,3}</?(?:${HTML_BLOCK_TAGS})(?=[\\s/>])`,
  "i",
);

function isGfmTableHeader(line: string): boolean {
  const trimmed = line.trim();
  return (
    trimmed.includes("|")
    && trimmed.replace(/^\|/, "").replace(/\|$/, "").trim() !== ""
  );
}

function isGfmTableDelimiter(line: string): boolean {
  const trimmed = line.trim();
  if (!trimmed.includes("|")) return false;

  const cells = trimmed.replace(/^\|/, "").replace(/\|$/, "").split("|");
  return (
    cells.length >= 2
    && cells.every((cell) => /^\s*:?-{1,}:?\s*$/.test(cell))
  );
}

/**
 * Returns whether text contains Markdown/HTML that renders as a block.
 *
 * The scanner intentionally examines line-start constructs only. That keeps
 * inline syntax such as `**bold**`, `[links](...)`, and `` `code` `` inside a
 * compacted tool run while recognizing headings, fences, quotes, tables,
 * horizontal rules, setext headings, and block HTML. Markdown list items are
 * intentionally treated as narration because agents commonly use them while
 * working through a tool run.
 */
export function hasBlockLevelMarkdown(text: string): boolean {
  if (text.trim() === "") return false;

  const lines = text.split(/\r\n?|\n/);
  for (let index = 0; index < lines.length; index += 1) {
    const line = lines[index];
    if (
      ATX_HEADING_RE.test(line)
      || FENCED_CODE_BLOCK_RE.test(line)
      || BLOCKQUOTE_RE.test(line)
      || HORIZONTAL_RULE_RE.test(line)
      || HTML_BLOCK_RE.test(line)
    ) {
      return true;
    }

    const previousLine = lines[index - 1];
    if (
      index > 0
      && previousLine.trim() !== ""
      && (SETEXT_UNDERLINE_RE.test(line)
        || (isGfmTableHeader(previousLine) && isGfmTableDelimiter(line)))
    ) {
      return true;
    }
  }

  return false;
}
